import { Area } from '../Area'
import { Distance } from '../Distance'

/**
 * Non-reactive shape instance manager
 *
 * This class manages MapShape instances (Area/Distance) separately from Pinia's reactive state.
 * It owns the shape instances and handles their lifecycle, while the Pinia store owns
 * the serializable state. This separation prevents circular dependencies and recursion issues.
 *
 * Key principles:
 * - Shape instances are stored in a plain Map (not reactive)
 * - Shapes communicate with store via CommandQueue (unidirectional)
 * - Registry syncs with store state via efficient diffing
 */
export class ShapeRegistry {
  constructor(map, commandQueue) {
    this.map = map
    this.commandQueue = commandQueue
    this.shapes = new Map() // Plain Map - NOT reactive!
  }

  /**
   * Create a new shape instance
   * @param {string} type - 'area' or 'distance'
   * @param {object} config - Shape configuration from store state
   * @returns {Area|Distance} The created shape instance
   */
  createShape(type, config) {
    const ShapeClass = type === 'area' ? Area : Distance

    const shape = new ShapeClass(this.map, {
      ...config,
      commandQueue: this.commandQueue
    })

    this.shapes.set(shape.indexID, shape)
    return shape
  }

  /**
   * Delete a shape instance and clean up
   * @param {number|string} indexID - The shape's index ID
   */
  deleteShape(indexID) {
    const shape = this.shapes.get(indexID)
    if (shape) {
      // Call shape's delete method to clean up Google Maps objects
      shape.delete()
      this.shapes.delete(indexID)
    }
  }

  /**
   * Get a shape instance by ID
   * @param {number|string} indexID - The shape's index ID
   * @returns {Area|Distance|undefined} The shape instance or undefined
   */
  getShape(indexID) {
    return this.shapes.get(indexID)
  }

  /**
   * Get all shape instances
   * @returns {Array<Area|Distance>} Array of all shape instances
   */
  getAllShapes() {
    return Array.from(this.shapes.values())
  }

  /**
   * Check if a shape exists in the registry
   * @param {number|string} indexID - The shape's index ID
   * @returns {boolean}
   */
  hasShape(indexID) {
    return this.shapes.has(indexID)
  }

  /**
   * Sync registry with store state using efficient diffing
   * Only creates, updates, or deletes shapes that have actually changed
   *
   * @param {Map} shapesData - The shapesData Map from the store
   */
  syncWithStore(shapesData) {
    const storeIDs = new Set(shapesData.keys())
    const registryIDs = new Set(this.shapes.keys())

    // Step 1: Delete shapes that no longer exist in store
    for (const id of registryIDs) {
      if (!storeIDs.has(id)) {
        console.log('ShapeRegistry: Deleting removed shape', id)
        this.deleteShape(id)
      }
    }

    // Step 2: Create new shapes that don't exist in registry
    for (const id of storeIDs) {
      if (!registryIDs.has(id)) {
        const state = shapesData.get(id)
        console.log('ShapeRegistry: Creating new shape', id, state.type)
        this.createShape(state.type, state)
      }
    }

    // Step 3: Update existing shapes (only if state has changed)
    for (const id of storeIDs) {
      if (registryIDs.has(id)) {
        const shape = this.shapes.get(id)
        const newState = shapesData.get(id)

        // Use updateFromState which has its own change detection
        // (compares JSON.stringify of current state vs new state)
        shape.updateFromState(newState)
      }
    }
  }

  /**
   * Clear all shapes from the registry
   */
  clear() {
    // Delete all shapes to clean up Google Maps objects
    for (const indexID of this.shapes.keys()) {
      this.deleteShape(indexID)
    }
  }

  /**
   * Get the count of shapes in the registry
   * @returns {number}
   */
  get count() {
    return this.shapes.size
  }
}
